import {
  ContentfulCloudinaryDataNeed,
  ContentfulDataNeed,
  ContentfulOtherDataNeed,
  PluginData,
  PluginDataNeeds,
  SupportedPluginContentfulDataType,
} from "./PluginRegistryTypes";
import { queryContentful } from "../utils/contentfulQueryBuilder";
import PluginRegistry from "./PluginRegistry";
import { plugins } from "../plugins";
import { getCloudinaryImageEntry, getEntry } from "../services/merchContentService";

export const getPluginData = (
  componentName: string,
  dataNeeds: PluginDataNeeds | undefined,
  allPluginsData: PluginData
): PluginData => {
  const pluginData: PluginData = { contentful: {} };
  if (dataNeeds) {
    Object.keys(dataNeeds.contentful).forEach(dataName => {
      const lookupKey = `${componentName}_${dataName}`;
      const data = allPluginsData.contentful?.[lookupKey];
      if (data) {
        pluginData.contentful[dataName] = data;
      }
    });
  }
  return pluginData;
};

export const resolvePluginData = async (
  pluginRegistry: PluginRegistry,
  locale: string,
  graphql: Function
): Promise<PluginData> => {
  try {
    const allDataNeeds = pluginRegistry.getAllDataNeeds();
    const allContentfulData = await getAllPluginsContentfulData(
      allDataNeeds.contentful,
      locale,
      graphql
    );
    return {
      contentful: allContentfulData,
    };
  } catch (err) {
    console.error(err);
    return { contentful: {} };
  }
};

const getAllPluginsContentfulData = async (
  dataNeeds: Record<string, ContentfulDataNeed>,
  locale: string,
  graphql: Function
) => {
  const dictionaryEntries: Record<string, ContentfulDataNeed> = {};
  const dictionaryEntryMarkdowns: Record<string, ContentfulDataNeed> = {};
  const images: Record<string, ContentfulCloudinaryDataNeed> = {};
  const simpleLinks: Record<string, ContentfulDataNeed> = {};
  const videos: Record<string, ContentfulDataNeed> = {};
  const other: Record<string, ContentfulOtherDataNeed> = {};

  Object.keys(dataNeeds).map(dataName => {
    const dataNeed = dataNeeds[dataName];
    switch (dataNeed.contentType) {
      case SupportedPluginContentfulDataType.DictionaryEntry: {
        dictionaryEntries[dataName] = dataNeed;
        break;
      }
      case SupportedPluginContentfulDataType.DictionaryEntryMarkdown: {
        dictionaryEntryMarkdowns[dataName] = dataNeed;
        break;
      }
      case SupportedPluginContentfulDataType.CloudinaryImageComponent: {
        images[dataName] = dataNeed;
        break;
      }
      case SupportedPluginContentfulDataType.CloudinaryVideoComponent: {
        videos[dataName] = dataNeed;
        break;
      }
      case SupportedPluginContentfulDataType.SimpleLink: {
        simpleLinks[dataName] = dataNeed;
        break;
      }
      case SupportedPluginContentfulDataType.Other: {
        other[dataName] = dataNeed as ContentfulOtherDataNeed;
        break;
      }
    }
  });

  const data = await queryContentful({
    graphql,
    locale,
    dictionaryEntries,
    dictionaryEntryMarkdowns,
    simpleLinks,
    images,
    videos,
    other,
  });
  return data;
};

export const getAllPluginsMCSData = async (
  dataNeeds: Record<string, ContentfulDataNeed>,
  tenant: string,
  localeUnknownCase: string
) => {
  const result: Record<string, any> = {};
  const locale = localeUnknownCase.toLowerCase();
  for (const [dataName, dataNeed] of Object.entries(dataNeeds)) {
    if (
      [SupportedPluginContentfulDataType.CloudinaryVideoComponent].includes(dataNeed.contentType)
    ) {
      console.log("TYPE NOT SUPPORTED", dataNeed);
      continue;
    }
    try {
      let value;
      if (dataNeed.contentType === SupportedPluginContentfulDataType.CloudinaryImageComponent) {
        const imgDataNeed = dataNeed as ContentfulCloudinaryDataNeed;
        result[dataName] = await getCloudinaryImageEntry(
          tenant,
          locale,
          dataNeed.contentfulName!,
          imgDataNeed.transformationsMcs,
          imgDataNeed.transformationsMcsView
        );
        continue;
      } else if (dataNeed.contentType == SupportedPluginContentfulDataType.Other) {
        const otherDataNeed = dataNeed as ContentfulOtherDataNeed;
        value = await getEntry(
          tenant,
          otherDataNeed.contentTypeName,
          otherDataNeed.contentfulName!
        );
      } else {
        value = await getEntry(tenant, dataNeed.contentType, dataNeed.contentfulName!);
      }

      result[dataName] = value[locale];
    } catch (e) {
      console.log("Failed to get dataNeed", dataName, dataNeed, e);
      throw e;
    }
  }

  return { contentful: result };
};
